"use client";

import { useLanguage } from "../context/LanguageContext";

type CookieBannerProps = {
  visible: boolean;
  onAccept: () => void;
  onReject: () => void;
  onCustomize: () => void;
};

export function CookieBanner({
  visible,
  onAccept,
  onReject,
  onCustomize
}: CookieBannerProps): JSX.Element | null {
  const { t } = useLanguage();
  const copy = t.cookie.banner;

  if (!visible) {
    return null;
  }

  return (
    <aside
      role="region"
      aria-label="Cookie consent"
      aria-describedby="cookie-consent-message"
      className="fixed inset-x-4 bottom-4 z-50 md:bottom-6 pointer-events-none"
    >
      <div className="relative mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/90 pointer-events-auto">
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          <p
            id="cookie-consent-message"
            aria-live="polite"
            className="text-sm leading-relaxed text-slate-800 dark:text-slate-100"
          >
            {copy.message}
          </p>
          <div className="flex flex-wrap items-center gap-2 sm:justify-end">
            <button
              type="button"
              onClick={onCustomize}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {copy.customize}
            </button>
            <button
              type="button"
              onClick={onReject}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {copy.reject}
            </button>
            <button
              type="button"
              onClick={onAccept}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-primary-500 dark:hover:bg-primary-400"
            >
              {copy.accept}
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}
